
export interface ICustomErrorProps {
  message?: string;
  innerError?: Error;
}

/**
 * Base class for custom errors.
 * Derive sub-classes from this class to implement errors which support
 * strongly-typed constructors and visible fields.
 *
 * The pattern to extend Error properly is documented here:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error
 */
export class CustomError implements Error {
  public readonly innerError?: Error;
  public name!: string;
  public message: string;
  public stack?: string;

  constructor(props: ICustomErrorProps = {}) {
    // The real error will be returned.
    const error = new Error(props.message) as CustomError;

    const customErrorType = Object.getPrototypeOf(this);

    this.message = props.message || error.message;

    // @ts-ignore
    if (Error.captureStackTrace) {
      // @ts-ignore
      Error.captureStackTrace(error, customErrorType);
    }

    if (Object.setPrototypeOf) {
      // Force the error instance to use the custom prototype.
      // This works on all supported browsers, except PhantomJS.
      Object.setPrototypeOf(error, customErrorType);
      return error;
    }
  }
}

// For Error, need to manually extend the built-in type since TypeScript does not emit the necessary output.

if (Object.setPrototypeOf) {
  // This works on all supported browsers, except PhantomJS.
  CustomError.prototype = Object.create(Error.prototype, {
    constructor: {
      value: Error,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });

  Object.setPrototypeOf(CustomError, Error);
}
